import { useAuth, useEventMemberships } from '@shared/components/AuthProvider';
import { useAddItemToRegistryAttempt } from './useAddItemToRegistryAttempt';
import { useAutoAssociateUserToEvent } from './useAutoAssociateUserToEvent';
import { useEventAssociator } from './useEventAssociator';
import { useEventIdFromContext } from './useEventIdFromContext';
import { usePublicShopRootQuery } from '@graphql/queries/publicShop.graphql.generated';
import { useCashOnlyEvents } from '@apps/registry/common/hooks/useCashOnlyEvents';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useToast } from '@withjoy/joykit';

export const useShopController = () => {
  const { currentUser, hasInitializedOnce } = useAuth();
  const { eventMemberships, loading: isLoadingEventMemberships } = useEventMemberships(undefined, { includeRegistryEligibility: true });
  const { addToRegistryAttemptInfo, trackAttempt, resetAttempt } = useAddItemToRegistryAttempt();

  const initialEventId = useEventIdFromContext();

  const { eventId, showEventPickerDialog, onSingleMatch, onZeroOrMultipleMatches } = useEventAssociator({ addToRegistryAttemptInfo });

  const { data, loading: isLoadingEventData } = usePublicShopRootQuery({ variables: { eventId: eventId || '', includeEvent: !!eventId }, batchMode: 'fast' });

  useAutoAssociateUserToEvent({
    eventId,
    eventMemberships,
    hasAuthenticatedOnce: hasInitializedOnce && !isLoadingEventMemberships,
    initialEventId,
    onSingleMatch,
    onZeroOrMultipleMatches,
  });

  const { cashOnlyEventIds } = useCashOnlyEvents(eventMemberships);
  const isCashOnlyEventSelected = !!eventId && cashOnlyEventIds.has(eventId);

  const { toast } = useToast();
  const { tq } = useTranslation('catalogRegistry');
  const cashOnlyMessage = tq`Your event is outside the US, where the Joy store isn't available yet. You can still add cash funds, and gifts from any other store.`;
  const handleDeliverableAddRefused = useEventCallback(() => {
    toast(cashOnlyMessage);
    // A refusal consumes the attempt. Deliverable attempts are otherwise only cleared by a
    // successful add, so leaving it set lets useRetryAddCatalogItemAttempt replay the product
    // against the next event the user picks, with no fresh click.
    resetAttempt();
  });

  return {
    currentUser,
    eventId: isLoadingEventData ? undefined : eventId,
    eventType: data?.eventById?.info?.eventType || null,
    handleOnAddItemToRegistryWithoutEvent: trackAttempt,
    onDeliverableAddRefused: isCashOnlyEventSelected ? handleDeliverableAddRefused : undefined,
    addToRegistryAttemptInfo,
    removeAddAttemptInfo: resetAttempt,
    showEventPickerDialog,
    shippingAddress: data?.eventById?.registry?.shippingAddress || null,
    registryCurrency: data?.eventById?.registry?.currency || null,
    finalizedDate: data?.eventById?.info?.finalizedDate || null,
    registries: data?.eventById?.info?.registry || null,
    orders: data?.eventById?.registry?.orders,
    website: data?.eventById?.website || null,
    finalizedEventDate: data?.eventById?.info?.finalizedDate,
  } as const;
};
